import React, { useState, createContext, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink, useLocation } from 'react-router-dom';
import UserInput from './UserInput';
import Login from './Login';
import ProtectedRoute from './ProtectedRoute';
import { useAuth } from './AuthProvider';
import { usePermissions } from './rbac/PermissionsProvider';
import RequirePermission from './rbac/RequirePermission';
import { PERMISSIONS } from './rbac/permissions';
import { useMsal } from '@azure/msal-react';
import { getApiToken } from './authUtils';
import MenuIcon from '@mui/icons-material/Menu';

import UserDashboard from './dashboards/UserDashboard';
import ApprovalDashboard from './approvals/ApprovalDashboard';
import AdminDashboard from './dashboards/AdminDashboard';
import AnalyticsDashboard from './dashboards/AnalyticsDashboard';
import TransportationDashboard from './dashboards/TransportationDashboard';
import SampleDashboard from './dashboards/SampleDashboard';
import CustomizationDashboard from './dashboards/CustomizationDashboard';
import MarketingDashboard from './dashboards/MarketingDashboard';
import PurchaseInvoiceDashboard from './dashboards/PurchaseInvoiceDashboard';
import SalesInvoiceDashboard from './dashboards/SalesInvoiceDashboard';
import LegalClientsDashboard from './components/LegalClientsdashboard';

// --- IMPORT ADMIN TOOLS ---
import AdminTools from './AdminTools';

import SmartSearchPage from './pages/SmartSearchPage';
import SmartProductDetail from './pages/SmartProductDetail';


import {
    AppBar, Toolbar, Button, Typography, Box,
    List, ListItem, ListItemButton, ListItemIcon, ListItemText, Drawer, Tooltip, CircularProgress, useTheme,IconButton,useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PostAddIcon from '@mui/icons-material/PostAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BuildIcon from '@mui/icons-material/Build'; // Icon for Admin Tools
import GiftBuilder from './components/GiftBuilder';
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CampaignIcon from '@mui/icons-material/Campaign';

import './print.css';
import UnifiedDashboard from './dashboards/unifiedDashboard';
import LegalVendorsDashboard from './components/LegalVendorsdashboard';
import ResourceManagement from './components/ResourceMangement';
import LeadBlockManagement from './dashboards/LeadBlockManagement';
import VendorCatalogFinder from './components/VendorCatalogFinder';

const urlconfig = require('./config/urlconfig');

export const ItemsContext = createContext();

const theme = createTheme({
    palette: {
      primary: {
        main: '#007bff',
        light: '#e6f7ff',
        dark: '#0056b3',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#6c757d',
        light: '#e2e3e5',
        dark: '#383d41',
        contrastText: '#ffffff',
      },
      error: {
        main: '#dc3545',
        light: '#f8d7da',
      },
      success: {
        main: '#28a745',
        light: '#d4edda',
      },
      warning: {
        main: '#ffc107',
        light: '#fff3cd',
      },
      background: {
        default: '#f4f7f6',
        paper: '#ffffff',
      },
      text: {
        primary: '#333333',
        secondary: '#555555',
        disabled: '#888888',
      },
      divider: '#e0e0e0',
      },
    typography: {
      fontFamily: [
        'Lato',
        'Open Sans',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
            fontSize: '2rem',
            fontWeight: 700,
            color: '#333333',
          },
          h2: {
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#333333',
          },
          h3: {
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#007bff',
          },
          h4: {
            fontSize: '1.1rem',
            fontWeight: 600,
            color: '#333333',
          },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
        color: '#333333',
      },
      body2: {
        fontSize: '0.9rem',
        lineHeight: 1.5,
        color: '#555555',
      },
      caption: {
        fontSize: '0.8em',
        color: '#888888',
      },
      metricLabel: {
        fontSize: '0.9em',
        color: '#555555',
        textTransform: 'capitalize',
        fontWeight: 400,
      },
      enquiryValue: {
         fontSize: '1.1rem',
         fontWeight: 700,
         color: '#0056b3',
      },
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 5,
            textTransform: 'none',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
            root: {
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#007bff',
                },
            },
        },
      },
      MuiSelect: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: '#007bff',
            color: '#ffffff',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: '#e9ecef',
            color: '#333333',
            fontWeight: 'bold',
          },
        },
      },
      MuiTableContainer: {
          styleOverrides: {
              root: {
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
              },
          },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            boxShadow: '0 5px 20px rgba(0,0,0,0.2)',
          },
        },
      },
      MuiTypography: {
        variants: [
          {
            props: { variant: 'metricLabel' },
            style: {

            },
          },
          {
             props: { variant: 'enquiryValue' },
             style: {

             },
          },
        ],
      },
    },
});

function Sidebar({ isMobile, mobileOpen, onDrawerToggle }) {
    const { can } = usePermissions();
    const location = useLocation();
    const collapsedWidth = 70;

    const menuItems = [
        {
            text: 'Smart Search',
            icon: <ManageSearchIcon />,
            path: '/smart-search',
            highlight: true
        },
        { text: 'New Cost Sheet', icon: <PostAddIcon />, path: '/' },
        // { text: 'User Dashboard', icon: <PeopleIcon />, path: '/user-dashboard' },
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'Samples', icon: <InventoryIcon />, path: '/samples-dashboard' },
        { text: 'Sale Invoice', icon: <ReceiptLongIcon/>, path:'/sales-invoice-dashboard'},
        { text: 'Purchase Invoice', icon: <LocalPrintshopIcon/>, path:'/purchase-invoice-dashboard'},
        { text: 'Vendor Catalogs', icon: <MenuBookIcon/>, path:'/vendor-catalogs'}
    ];

    menuItems.push({ text: 'Transportation', icon: <LocalShippingIcon />, path: '/transportation-dashboard' });
    menuItems.push({ text: 'Customization', icon: <SettingsIcon />, path: '/customization-dashboard' });
    menuItems.push({ text: 'Marketing', icon: <CampaignIcon />, path: '/marketing-dashboard' });

    if (can(PERMISSIONS.ANALYTICS_VIEW)) {
        menuItems.push({ text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics-dashboard' });
    }
    if (can(PERMISSIONS.COSTSHEET_APPROVE)) {
        menuItems.push({ text: 'Approvals', icon: <CheckCircleIcon />, path: '/approval-dashboard' });
    }
    if (can(PERMISSIONS.ADMIN_TOOLS)) {
        menuItems.push({ text: 'Admin Tools', icon: <BuildIcon />, path: '/admin-tools' });
    }

    const drawerContent = (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Toolbar />
            <Box sx={{
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 1,
                flexGrow: 1,
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                '-ms-overflow-style': 'none',
            }}>
                <List>
                    {/* --- 2. STANDARD MENU ITEMS (Loop) --- */}
                    {menuItems.map((item) => (
                        <ListItem key={item.text} disablePadding sx={item.highlight ? { mb: 1, borderBottom: '1px solid #e0e0e0', pb: 1 } : {}}>
                            <Tooltip title={isMobile ? '' : item.text} placement="right">
                                <ListItemButton
                                    component={RouterLink}
                                    to={item.path}
                                    selected={location.pathname === item.path}
                                    onClick={isMobile ? onDrawerToggle : null}
                                    sx={{
                                        justifyContent: isMobile ? 'flex-start' : 'center',
                                        ...(item.highlight && {
                                            backgroundColor: '#e3f2fd',
                                            '&:hover': { backgroundColor: '#bbdefb' },
                                            '&.Mui-selected': { backgroundColor: '#bbdefb' },
                                            borderRadius: '8px',
                                            border: '1px solid #90caf9'
                                        })
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        minWidth: isMobile ? 'auto' : 0,
                                        color: item.highlight ? '#1976d2' : 'text.secondary',
                                        mr: isMobile ? 3 : 0,
                                        pl: isMobile ? 0 : (item.highlight ? 0 : 1)
                                    }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    {isMobile && <ListItemText primary={item.text} sx={{ color: item.highlight ? '#1565c0' : 'text.primary', fontWeight: item.highlight ? 'bold' : 'normal' }} />}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    ))}
                </List>
            </Box>
        </div>
    );

    return (
        <Box
            component="nav"
            className="no-print" // <--- ADDED NO-PRINT HERE TO HIDE THE SIDEBAR
            sx={{ width: { sm: collapsedWidth }, flexShrink: { sm: 0 } }}
        >
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 } }}
            >
                {drawerContent}
            </Drawer>
            <Drawer
                variant="permanent"
                sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: collapsedWidth, overflowX: 'hidden' } }}
                open
            >
                {drawerContent}
            </Drawer>
        </Box>
    );
}

function MainLayout({ children }) {
    const { username, signOut } = useAuth();
    const handleLogout = () => signOut();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <Sidebar isMobile={isMobile} mobileOpen={mobileOpen} onDrawerToggle={handleDrawerToggle} />
            <Box
                component="main"
                sx={{ flexGrow: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column', minWidth: 0 }}
            >
                {/* <--- ADDED NO-PRINT HERE TO HIDE THE TOP BAR ---> */}
                <AppBar className="no-print" position="static" color="default" elevation={0} sx={{ backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
                    <Toolbar>
                        {isMobile && (
                            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }} >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        {Login && (
                            <Box>
                            <Button
                                component={RouterLink}
                                to="/client-vendor-management"
                                variant="contained"
                                startIcon={<BusinessCenterIcon />}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    borderRadius: 3,
                                    px: 3,
                                    py: 1,
                                    mx:2,
                                    background: "linear-gradient(135deg, #1976d2, #1565c0)",
                                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        background: "linear-gradient(135deg, #1565c0, #0d47a1)",
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 8px 20px rgba(25, 118, 210, 0.4)",
                                    },
                                }}
                            >
                                Client & Vendor Management
                            </Button>

                            <Button
                                component={RouterLink}
                                to="/lead-block-management"
                                variant="contained"
                                startIcon={<LeaderboardRoundedIcon />}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    borderRadius: 3,
                                    px: 3,
                                    py: 1,
                                    mx:2,
                                    background: "linear-gradient(135deg, #1976d2, #1565c0)",
                                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        background: "linear-gradient(135deg, #1565c0, #0d47a1)",
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 8px 20px rgba(25, 118, 210, 0.4)",
                                    },
                                }}
                            >
                               Lead Block Dashboard
                            </Button>
                            </Box>
                        )}
                        <Typography sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>Welcome, {username}</Typography>
                        <Button variant="contained" color="error" startIcon={<LogoutIcon />} onClick={handleLogout}>
                            <span style={{ display: isMobile ? 'none' : 'inline' }}>Logout</span>
                        </Button>
                    </Toolbar>
                </AppBar>

                {/* Dashboard content gets rendered here */}
                <Box sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1, bgcolor: 'background.default', overflow: 'auto' }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
}

const App = () => {
    const { account, isLoadingAuth } = useAuth();
    const { instance } = useMsal();

    const [itemsData, setItemsData] = useState({ content: [], totalPages: 0, totalElements: 0, number: 0 });
    const [deliveryItems, setDeliveryItems] = useState({ content: [], totalPages: 0, totalElements: 0, number: 0 });
    const [loadingItems, setLoadingItems] = useState(false);
    const [itemsError, setItemsError] = useState(null);
    const [legalClients, setLegalClients] = useState([]);
    const [legalVendors, setLegalVendors] = useState([]);

    // Only the latest request may update the table, so a slow earlier search can't overwrite it
    const latestItemsRequest = useRef(0);
    const fetchAllItems = useCallback(async (search = '', page = 0, size = 10) => {
        const requestId = ++latestItemsRequest.current;
        setLoadingItems(true);
        setItemsError(null);
        if (!account) { // Don't fetch if not logged in
            setLoadingItems(false);
            return;
        }
        try {
            const accessToken = await getApiToken(instance, account);
            const response = await fetch(`${urlconfig.backend_api_base_url}/api/v2/items?search=${encodeURIComponent(search)}&page=${page}&size=${size}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
            if (!response.ok) {
                throw new Error('Failed to fetch items from the server');
            }
            const data = await response.json();
            if (requestId === latestItemsRequest.current) setItemsData(data);
        } catch (err) {
            console.error('Error fetching items for context:', err);
            setItemsError(`Failed to load data: ${err.message}`);
        } finally {
            setLoadingItems(false);
        }
    }, [account, instance]);

    const fetchAllItemsForAnalytics = useCallback(async () => {
        if (!account) return;
        setLoadingItems(true);
        setItemsError(null);
        try {
            const accessToken = await getApiToken(instance, account);
            const response = await fetch(`${urlconfig.backend_api_base_url}/api/v2/get-master-csv`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            if (!response.ok) {
                throw new Error('Failed to fetch all items for analytics');
            }
            const data = await response.json();
            setItemsData({ content: data, totalPages: 1, totalElements: data.length, number: 0 });
        } catch (err) {
            console.error('Error fetching all items for analytics:', err);
            setItemsError(`Failed to load analytics data: ${err.message}`);
        } finally {
            setLoadingItems(false);
        }
    }, [account, instance]);

     const fetchLegalClientsAndVendors = useCallback(async () => {
        if (!account) return;
        try {
            const accessToken = await getApiToken(instance, account);
            const headers = { 'Authorization': `Bearer ${accessToken}` };
            const [clientsRes, vendorsRes] = await Promise.all([
                fetch(`${urlconfig.backend_api_base_url}/api/v2/clients`, { headers }),
                fetch(`${urlconfig.backend_api_base_url}/api/v2/vendors`, { headers }),
            ]);
            const clientsData = clientsRes.ok ? await clientsRes.json() : [];
            const vendorsData = vendorsRes.ok ? await vendorsRes.json() : [];
            setLegalClients(clientsData || []);
            setLegalVendors(vendorsData || []);
        } catch (err) {
            console.error('Error fetching legal clients/vendors:', err);
        }
    }, [account, instance]);

    const fetchAllItemsForDeliveryAnalytics = useCallback(async (search = '') => {
        if (!account) return;
        setLoadingItems(true);
        setItemsError(null);
        try {
            const accessToken = await getApiToken(instance, account);
            // Slim rows (id, order no, client, SCM SPOC, status, dates, creator, cost sheet for) for the
            // User and Admin dashboards' delivery cards and lists, not whole cost sheets
            const response = await fetch(`${urlconfig.backend_api_base_url}/api/v2/dashboards/deliveries?search=${encodeURIComponent(search)}`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            if (!response.ok) {
                throw new Error('Failed to fetch all items for delivery analytics');
            }
            const data = await response.json();
            setDeliveryItems({ content: data, totalPages: 1, totalElements: data.length, number: 0 });
        } catch (err) {
            console.error('Error fetching all items for delivery analytics:', err);
            setItemsError(`Failed to load delivery analytics data: ${err.message}`);
        } finally {
            setLoadingItems(false);
        }
    }, [account, instance]);

    if (isLoadingAuth) {
        return (
            <ThemeProvider theme={theme}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                    <CircularProgress />
                    <Typography variant="h6" sx={{ ml: 2 }}>Loading authentication...</Typography>
                </Box>
            </ThemeProvider>
        );
    }

    const AuthLayout = ({ children }) => children;

    return (
        <ThemeProvider theme={theme}>
            <ItemsContext.Provider value={{
                items: itemsData,
                deliveryItems,
                loading: loadingItems,
                error: itemsError,
                refreshItems: fetchAllItems,
                fetchAllItemsForAnalytics,
                fetchAllItemsForDeliveryAnalytics,
                legalClients,
                legalVendors,
                refreshLegalClientsAndVendors: fetchLegalClientsAndVendors,
            }}>
                <Router>
                    <Routes>
                        <Route path="/login" element={<AuthLayout><Login /></AuthLayout>} />
                        <Route element={<ProtectedRoute />}>
                            <Route path="/*" element={
                                <MainLayout>
                                    <Routes>
                                        <Route path="/" element={<UserInput />} />
                                        <Route path="/user-dashboard" element={<UserDashboard />} />
                                        <Route path="/cost-sheet-dashboard" element={<RequirePermission permission={PERMISSIONS.COSTSHEET_VIEW_ALL}><AdminDashboard /></RequirePermission>} />
                                        <Route path="/analytics-dashboard" element={<RequirePermission permission={PERMISSIONS.ANALYTICS_VIEW}><AnalyticsDashboard /></RequirePermission>} />
                                        <Route path="/transportation-dashboard" element={<TransportationDashboard />} />
                                        <Route path="/customization-dashboard" element={<CustomizationDashboard />} />
                                        <Route path="/approval-dashboard" element={<RequirePermission permission={PERMISSIONS.COSTSHEET_APPROVE}><ApprovalDashboard /></RequirePermission>} />
                                        <Route path="/marketing-dashboard" element={<MarketingDashboard/>}/>
                                        <Route path="/samples-dashboard" element={<SampleDashboard />} />
                                        <Route path="/sales-invoice-dashboard"  element={<SalesInvoiceDashboard/>} />
                                        <Route path="/purchase-invoice-dashboard"  element={<PurchaseInvoiceDashboard/>} />
                                        <Route path="/dashboard" element={<UnifiedDashboard/>}/>
                                        <Route path="/client-vendor-management" element={<ResourceManagement/>}/>
                                        <Route path="/lead-block-management" element={<LeadBlockManagement/>}/>
                                        <Route path="/vendor-catalogs" element={<VendorCatalogFinder/>}/>

                                        {/* ADMIN TOOLS ROUTE */}
                                        <Route path="/admin-tools" element={<RequirePermission permission={PERMISSIONS.ADMIN_TOOLS}><AdminTools /></RequirePermission>} />

                                        {/* Smart Search Routes */}
                                        <Route path="/smart-search" element={<SmartSearchPage />} />
                                        <Route path="/smart-search/product/:id" element={<SmartProductDetail />} />
                                        <Route path="/gift-builder" element={<GiftBuilder />} />
                                        <Route path="*" element={<Typography variant="h4">404 - Not Found</Typography>} />
                                    </Routes>
                                </MainLayout>
                            } />
                        </Route>
                    </Routes>
                </Router>
            </ItemsContext.Provider>
        </ThemeProvider>
    );
};

export default App;